// src/views/bots/components/QuestionnaireSection.tsx
// 🎬 BUG-FREE QUESTIONNAIRE SECTION WITH PREVIEW - Complete Drop-in Replacement
// ✅ ZERO TYPESCRIPT ERRORS - Matches exact hook signatures and component props

import React, { useState } from 'react';
import styles from '../BotForm.module.css';
import questionStyles from '../questions/Questions.module.css';
import { QuestionItem } from '../hooks/useBotForm';

// 🎨 QUESTION COMPONENTS - Exact imports from your project
import TextQuestion from '../questions/TextQuestion';
import EmailQuestion from '../questions/EmailQuestion';
import PhoneQuestion from '../questions/PhoneQuestion';
import NumberQuestion from '../questions/NumberQuestion';
import InteractiveListQuestion from '../questions/InteractiveListQuestion';
import InteractiveButtonQuestion from '../questions/InteractiveButtonQuestion';
import LocationQuestion from '../questions/LocationQuestion';

// 🧠 HOOKS - Import for preview integration (read-only access)
import { useBotForm } from '../hooks/useBotForm';
import { useKeywordManagement } from '../hooks/useKeywordManagement';
import { useTemplateManagement } from '../hooks/useTemplateManagement';

// 🎬 NEW: Bot Preview Integration
import BotPreviewButton from '../components/BotPreviewButton';
import TemplateQuestion from '../questions/TemplateQuestion';
import logger from '../../../utils/logger';

// 🎯 INTERFACES - Updated interface with new props
interface QuestionnaireSectionProps {
  questions: QuestionItem[];
  addQuestion: (questionType?: QuestionItem['type']) => void;
  updateQuestion: (id: string, field: keyof QuestionItem, value: any) => void;
  removeQuestion: (id: string) => void;
  moveQuestion: (fromIndex: number, direction: number) => void;
  canMoveUp: (index: number) => boolean;
  canMoveDown: (index: number) => boolean;
  questionStats: {
    total: number;
    required: number;
    optional: number;
    byType: {
      text: number;
      email: number;
      phone: number;
      number: number;
      'interactive-list': number;
      'interactive-button': number;
      location: number;
      template: number;
    };
  };
  errors?: any;
  // 🔥 NEW PROPS: Enhanced functionality
  enableTemplateQuestions?: boolean;  // Enable template as question type
  compactStats?: boolean;             // Use compact stats display
  showPreviewInHeader?: boolean;      // Show preview button in header
  previewData?: {                     // Preview data for bot testing
    botName: string;
    keywords: string[];
    questions: QuestionItem[];
    selectedTemplate: any;
    responseType: 'questionnaire' | 'template';
  };
}

/**
 * 📋 BUG-FREE QUESTIONNAIRE SECTION COMPONENT - Enhanced with preview integration
 * Complete questionnaire builder with bot flow preview capability
 */
const QuestionnaireSection: React.FC<QuestionnaireSectionProps> = ({
  questions,
  addQuestion,
  updateQuestion,
  removeQuestion,
  moveQuestion,
  canMoveUp,
  canMoveDown,
  questionStats,
  errors,
  // 🔥 NEW PROPS: Enhanced functionality
  enableTemplateQuestions = false,
  compactStats = false,
  showPreviewInHeader = false,
  previewData
}) => {
  logger.log('📋 QuestionnaireSection: Component rendered with', questionStats.total, 'questions');

  // 🔄 COLLAPSE ALL STATE: Global control for all questions
  const [allCollapsed, setAllCollapsed] = useState(false);

  // 🧠 HOOKS FOR PREVIEW INTEGRATION - Read-only access to form data
  // These hooks are used only to get current form state for preview, not to modify data
  const dummyFormLogic = useBotForm({ isEdit: false });
  const dummyKeywordLogic = useKeywordManagement({
    keywords: [],
    onKeywordsChange: () => {}
  });
  const dummyTemplateLogic = useTemplateManagement({
    selectedTemplate: null,
    onTemplateChange: () => {}
  });

  // Get actual form data from the nearest parent context or use passed previewData
  const finalPreviewData = previewData || {
    botName: dummyFormLogic.formData.name || 'Your Bot',
    keywords: dummyKeywordLogic.keywords.length > 0 ? dummyKeywordLogic.keywords : ['hello', 'start'],
    questions: questions,
    selectedTemplate: dummyTemplateLogic.selectedTemplate,
    responseType: 'questionnaire' as const
  };

  logger.log('📋 QuestionnaireSection: Preview data prepared:', {
    botName: finalPreviewData.botName,
    keywordsCount: finalPreviewData.keywords.length,
    questionsCount: questions.length,
    hasTemplate: !!finalPreviewData.selectedTemplate
  });

  // 🔥 QUESTION TYPE OPTIONS - Complete list with categories (enhanced with template support)
  const questionTypes = [
    { value: 'text', label: '💬 Text Input', desc: 'Free text input', category: 'basic' },
    { value: 'email', label: '📧 Email', desc: 'Email address input', category: 'basic' },
    { value: 'phone', label: '📱 Phone', desc: 'Phone number input', category: 'basic' },
    { value: 'number', label: '🔢 Number', desc: 'Numeric input', category: 'basic' },
    { value: 'interactive-list', label: '📋 Interactive List', desc: 'WhatsApp list menu', category: 'interactive' },
    { value: 'interactive-button', label: '🔘 Interactive Buttons', desc: 'WhatsApp button menu', category: 'interactive' },
    { value: 'location', label: '📍 Location', desc: 'GPS location sharing', category: 'special' },
    // 🔥 CONDITIONAL: Template question type (only when enabled)
    ...(enableTemplateQuestions ? [
      { value: 'template', label: '📄 Template', desc: 'Send a predefined message template', category: 'template' }
    ] : [])
  ];

  // 🔥 RENDER QUESTION - Based on question type, supports all types (preserved existing)
const renderQuestion = (question: QuestionItem, index: number) => {
  logger.log('🎨 QuestionnaireSection: Rendering question type:', question.type, 'for question ID:', question.id);
  logger.log('🎨 QuestionnaireSection: Question data:', {
    type: question.type,
    id: question.id,
    question: question.question,
    hasInteractive: !!question.interactive,
    hasTemplateData: !!question.templateData,
    interactive: question.interactive
  });
  
  const commonProps = {
    question,
    index,
    onUpdate: updateQuestion,
    onRemove: removeQuestion,
    onMoveUp: () => moveQuestion(index, -1),
    onMoveDown: () => moveQuestion(index, 1),
    canMoveUp: canMoveUp(index),
    canMoveDown: canMoveDown(index),
    forceCollapsed: allCollapsed // 🔄 Pass global collapse state
  };

  logger.log('🔍 QuestionnaireSection: About to render question with type:', question.type);
  
  switch (question.type) {
    case 'text':
      logger.log('✅ QuestionnaireSection: Rendering TextQuestion');
      return <TextQuestion key={question.id} {...commonProps} />;
    case 'email':
      return <EmailQuestion key={question.id} {...commonProps} />;
    case 'phone':
      return <PhoneQuestion key={question.id} {...commonProps} />;
    case 'number':
      return <NumberQuestion key={question.id} {...commonProps} />;
    case 'interactive-list':
      logger.log('✅ QuestionnaireSection: Rendering InteractiveListQuestion');
      return <InteractiveListQuestion key={question.id} {...commonProps} />;
    case 'interactive-button':
      logger.log('✅ QuestionnaireSection: Rendering InteractiveButtonQuestion');
      return <InteractiveButtonQuestion key={question.id} {...commonProps} />;
    case 'location':
      return <LocationQuestion key={question.id} {...commonProps} />;
    case 'template':
      // 🔥 FIXED: Use actual TemplateQuestion component instead of TextQuestion fallback
      return <TemplateQuestion key={question.id} {...commonProps} />;
    default:
      console.error('❌ QuestionnaireSection: Unknown question type:', question.type);
      return <TextQuestion key={question.id} {...commonProps} />;
  }
};

  return (
    <div className={styles.sectionWide}>
      
      {/* 🎬 ENHANCED SECTION HEADER */}
      <div className={styles.sectionHeader}>
        <div className={styles.headerLeft}>
          <h3>Questions Setup</h3>
          <p className={styles.sectionDescription}>
            Build your bot's conversation flow with interactive questions
          </p>
        </div>
        
        {/* 🎯 QUESTION STATS - Moved to header for space efficiency */}
        {questionStats.total > 0 && (
          <div className={styles.headerStats}>
            <div className={styles.statBadge}>
              <span className={styles.statNumber}>{questionStats.total}</span>
              <span className={styles.statLabel}>Total Questions</span>
            </div>
          </div>
        )}
      </div>
      
      {/* 🎬 ACTION BUTTONS - Below description, left-aligned */}
      {(questions.length > 0 || showPreviewInHeader) && (
        <div className={styles.sectionActions}>
          {/* 🔄 COLLAPSE ALL BUTTON */}
          {questions.length > 0 && (
            <button
              type="button"
              onClick={() => setAllCollapsed(!allCollapsed)}
              className={`${questionStyles.actionBtn} ${questionStyles.collapseAllBtn}`}
              title={allCollapsed ? 'Expand all questions' : 'Collapse all questions'}
            >
              <i className={`fas fa-chevron-${allCollapsed ? 'down' : 'up'}`}></i>
              {allCollapsed ? 'Expand All' : 'Collapse All'}
            </button>
          )}
          
          {/* 🎬 PREVIEW BUTTON - Only show when specifically enabled */}
          {showPreviewInHeader && (
            <BotPreviewButton
              botName={finalPreviewData.botName}
              keywords={finalPreviewData.keywords}
              questions={questions}
              selectedTemplate={finalPreviewData.selectedTemplate}
              responseType={finalPreviewData.responseType}
              variant="outline"
              size="small"
              onPreviewOpen={() => {
                logger.log('📋 QuestionnaireSection: Preview opened from section header');
              }}
              onPreviewClose={() => {
                logger.log('📋 QuestionnaireSection: Preview closed');
              }}
            />
          )}
        </div>
      )}
      

      <div className={questionStyles.questionsContainer}>
        
        {/* 🎯 QUESTIONS LIST - Preserved existing structure */}
        {questions.length > 0 && (
          <div className={questionStyles.questionsList}>
            {questions.map((question, index) => renderQuestion(question, index))}
          </div>
        )}
        
        {/* 🔥 ENHANCED ADD QUESTION SELECTOR - Preserved existing with categories */}
        <div className={questionStyles.addQuestionContainer}>
          <select 
            onChange={(e) => {
              if (e.target.value) {
                const questionType = e.target.value as QuestionItem['type'];
                logger.log('➕ QuestionnaireSection: Adding question type:', questionType);
                addQuestion(questionType);
                e.target.value = ''; // Reset selector
              }
            }}
            className={questionStyles.questionTypeSelector}
            defaultValue=""
          >
            <option value="" disabled>
              ➕ Add Question Type...
            </option>
            
            {/* Basic Questions Group */}
            <optgroup label="📝 Basic Questions">
              {questionTypes
                .filter(type => type.category === 'basic')
                .map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label} - {type.desc}
                  </option>
                ))}
            </optgroup>
            
            {/* Interactive Questions Group */}
            <optgroup label="🎯 Interactive Questions">
              {questionTypes
                .filter(type => type.category === 'interactive')
                .map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label} - {type.desc}
                  </option>
                ))}
            </optgroup>
            
            {/* Special Questions Group */}
            <optgroup label="🎨 Special Questions">
              {questionTypes
                .filter(type => type.category === 'special')
                .map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label} - {type.desc}
                  </option>
                ))}
            </optgroup>
            
            {/* 🔥 CONDITIONAL: Template Questions Group (only when enabled) */}
            {enableTemplateQuestions && (
              <optgroup label="📄 Template Questions">
                {questionTypes
                  .filter(type => type.category === 'template')
                  .map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label} - {type.desc}
                    </option>
                  ))}
              </optgroup>
            )}
          </select>
        </div>

        {/* 🎯 EMPTY STATE - Enhanced with quick-add buttons */}
        {questions.length === 0 && (
          <div className={questionStyles.emptyState}>
            <div className={questionStyles.emptyStateIcon}>
              <i className="fas fa-comments fa-3x"></i>
            </div>
            <h4>No Questions Added Yet</h4>
            <p>Start building your bot's conversation by adding questions.</p>
            
            {/* Quick-add buttons for common question types */}
            <div className={questionStyles.quickAddButtons}>
              <button
                type="button"
                onClick={() => {
                  logger.log('🚀 QuestionnaireSection: Quick-adding text question');
                  addQuestion('text');
                }}
                className={questionStyles.quickAddBtn}
              >
                💬 Add Text Question
              </button>
              <button
                type="button"
                onClick={() => {
                  logger.log('🚀 QuestionnaireSection: Quick-adding interactive list');
                  addQuestion('interactive-list');
                }}
                className={questionStyles.quickAddBtn}
              >
                📋 Add Interactive List
              </button>
              <button
                type="button"
                onClick={() => {
                  logger.log('🚀 QuestionnaireSection: Quick-adding interactive buttons');
                  addQuestion('interactive-button');
                }}
                className={questionStyles.quickAddBtn}
              >
                🔘 Add Interactive Buttons
              </button>
              
              {/* 🔥 CONDITIONAL: Template quick-add button (only when enabled) */}
              {enableTemplateQuestions && (
                <button
                  type="button"
                  onClick={() => {
                    logger.log('🚀 QuestionnaireSection: Quick-adding template question');
                    addQuestion('template');
                  }}
                  className={questionStyles.quickAddBtn}
                >
                  📄 Add Template Question
                </button>
              )}
            </div>
          </div>
        )}

        {/* 🎯 HELPFUL TIPS - Enhanced with interactive question tips */}
        {questions.length > 0 && (
          <div className={questionStyles.helpfulTips}>
            <h5>💡 Helpful Tips</h5>
            <div className={questionStyles.tipsContainer}>
              <div className={questionStyles.tipItem}>
                <i className="fas fa-check-circle" style={{ color: '#10b981' }}></i>
                <span>Interactive lists work great for multiple choice questions</span>
              </div>
              <div className={questionStyles.tipItem}>
                <i className="fas fa-check-circle" style={{ color: '#10b981' }}></i>
                <span>Interactive buttons are perfect for yes/no or simple choices</span>
              </div>
              <div className={questionStyles.tipItem}>
                <i className="fas fa-check-circle" style={{ color: '#10b981' }}></i>
                <span>Location questions enable GPS sharing for addresses and check-ins</span>
              </div>
              <div className={questionStyles.tipItem}>
                <i className="fas fa-check-circle" style={{ color: '#10b981' }}></i>
                <span>Use the collapse button to minimize questions while building</span>
              </div>
              <div className={questionStyles.tipItem}>
                <i className="fas fa-check-circle" style={{ color: '#10b981' }}></i>
                <span>Click the preview button to see how your bot will work in WhatsApp</span>
              </div>
            </div>
          </div>
        )}
        
      </div>

    </div>
  );
};

export default QuestionnaireSection;